import { Box, ExpandableSection, Table, TextContent } from "@amzn/awsui-components-react/polaris";
import { FC } from "react";
import { usePanelStore } from "../../state";

/** A queried feature (structural; mirrors the engine's QueriedFeature). */
interface QueriedFeature {
    id?: string | number;
    geometryType?: string;
    properties?: Record<string, unknown>;
    layerId?: string;
    source?: string;
    sourceLayer?: string;
}

interface InspectResult {
    lngLat?: { lng: number; lat: number };
    features?: QueriedFeature[];
}

/** Groups features by their style layer id (matches the 1.0 FeatureDisplay). */
function groupByLayer(features: QueriedFeature[]): Record<string, QueriedFeature[]> {
    const groups: Record<string, QueriedFeature[]> = {};
    features.forEach((f) => {
        const key = f.layerId ?? f.sourceLayer ?? f.source ?? "unknown";
        (groups[key] = groups[key] ?? []).push(f);
    });
    return groups;
}

/** A Cloudscape table of one layer's features: columns = property keys, rows = features. */
const FeatureTable: FC<{ features: QueriedFeature[] }> = ({ features }) => {
    const items = features.map((f) => f.properties ?? {});
    // Union of property keys across all features — features in the same layer can
    // have heterogeneous keys, so deriving columns from items[0] alone drops some.
    const columns = [...new Set(items.flatMap((item) => Object.keys(item)))];
    const columnDefinitions = columns.map((col) => ({
        id: col,
        header: col,
        cell: (item: Record<string, unknown>) => String(item[col] ?? ""),
        sortingField: col,
    }));
    return (
        <Table
            variant="embedded"
            columnDefinitions={columnDefinitions}
            items={items}
            sortingDisabled
            empty={<Box textAlign="center" color="inherit"><b>No properties.</b></Box>}
        />
    );
};

/**
 * Inspect "panel" mode results, rendered to match the 1.0 viewer: features are
 * grouped by layer into expandable sections, each showing a Cloudscape Table
 * with property keys as columns and one row per feature. Reads from the panel
 * store's data payload (pushed by InspectManager.onPanelResult).
 */
const FeatureInspectPanel: FC = () => {
    const data = usePanelStore((s) => s.panelData) as InspectResult | null;
    const features = data?.features ?? [];

    if (features.length === 0) {
        return <Box color="text-status-inactive">Click a feature on the map to inspect it.</Box>;
    }

    const byLayer = groupByLayer(features);

    return (
        <TextContent>
            {Object.keys(byLayer).map((layer) => (
                <ExpandableSection key={layer} headerText={layer} defaultExpanded>
                    <FeatureTable features={byLayer[layer]} />
                </ExpandableSection>
            ))}
        </TextContent>
    );
};

export default FeatureInspectPanel;
